import React, { useState, useContext, useEffect, useRef, useMemo } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  useMediaQuery,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Paper,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";

import { useStyle } from "../../context/styleContext/styleContext";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import HomePageProductCard from "../../components/ProductCard/HomePageProductCard";
import Seo from "../../components/Seo";

function ProductListSchemaMarkup({ products }) {
  useEffect(() => {
    if (!products || products.length === 0) return;

    const schemaData = {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: "Parish Audio Plugins",
      url: "https://parishaudio.com/plugins",
      mainEntity: {
        "@type": "ItemList",
        itemListElement: products.map((product, index) => ({
          "@type": "ListItem",
          position: index + 1,
          url: `https://parishaudio.com/products/${product.productURI}`,
          name: product.name,
        })),
      },
    };

    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.innerHTML = JSON.stringify(schemaData);
    document.head.appendChild(script);

    return () => {
      document.head.removeChild(script);
    };
  }, [products]);

  return null;
}

export default function Plugins() {
  const [height, setHeight] = useState(window.innerHeight);
  const [dataForAllProducts, setDataForAllProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [priceFilter, setPriceFilter] = useState("all");
  const [sortBy, setSortBy] = useState("featured");

  const { style } = useStyle();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const topContainer = useRef();

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  const getProductInfo = async () => {
    try {
      setLoading(true);
      const { data } = await axios.post("/api/get-products-available-on-website");
      if (data) {
        setDataForAllProducts(data);
      } else {
        console.log("data error");
      }
    } catch (error) {
      console.log("Error retrieving data...", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getProductInfo();
  }, []);

  const filteredProducts = useMemo(() => {
    let products = [...dataForAllProducts];

    const query = searchTerm.trim().toLowerCase();
    if (query) {
      products = products.filter((product) => {
        const searchableText = [
          product.name,
          product.shortDescription,
          product.cardSummary,
          product.longDescription,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();

        return searchableText.includes(query);
      });
    }

    if (priceFilter === "free") {
      products = products.filter((product) => Number(product.price) === 0);
    } else if (priceFilter === "paid") {
      products = products.filter((product) => Number(product.price) > 0);
    }

    if (sortBy === "name-asc") {
      products.sort((a, b) => a.name.localeCompare(b.name));
    } else if (sortBy === "name-desc") {
      products.sort((a, b) => b.name.localeCompare(a.name));
    } else if (sortBy === "price-low") {
      products.sort((a, b) => Number(a.price) - Number(b.price));
    } else if (sortBy === "price-high") {
      products.sort((a, b) => Number(b.price) - Number(a.price));
    } else if (sortBy === "rating") {
      products.sort((a, b) => {
        const aRating = Number(a.starRating || 0);
        const bRating = Number(b.starRating || 0);
        const aCount = Number(a.ratingCount || 0);
        const bCount = Number(b.ratingCount || 0);

        if (bRating !== aRating) return bRating - aRating;
        return bCount - aCount;
      });
    } else {
      // featured/default:
      // keep D1 Delay first, then original order for the rest
      products.sort((a, b) => {
        if (a.productURI === "d1-delay") return -1;
        if (b.productURI === "d1-delay") return 1;
        return 0;
      });
    }

    return products;
  }, [dataForAllProducts, searchTerm, priceFilter, sortBy]);

  return (
    <>
      <Seo
        title="Audio Plugins | Parish Audio"
        description="Browse all Parish Audio plugins for mixing, sound design, songwriting, and modern production. Explore free and paid VST3 plugins for Mac and Windows."
        canonical="https://parishaudio.com/plugins"
        image="https://parishaudio.com/assets/HeroBanner1.png"
        imageAlt="Parish Audio plugin collection"
        ogType="website"
      />

      {filteredProducts.length > 0 && (
        <ProductListSchemaMarkup products={filteredProducts} />
      )}

      <Box>
        <Box className="dark-alpha-80">
          <Box
            ref={topContainer}
            className="centered-main-container"
            sx={{
              width: "95%",
              maxWidth: "1200px",
              margin: "0 auto",
              minHeight: height - 130,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <Typography
              variant="h1"
              sx={{
                fontSize: { xs: "2rem", md: "2.6rem" },
                fontWeight: 800,
                mt: 2,
                mb: 1,
                color: style.color4,
              }}
            >
              All Plugins
            </Typography>

            <Typography
              variant="body1"
              sx={{
                color: style.color3,
                mb: 2,
                maxWidth: "800px",
                fontSize: "1.05rem",
                margin:'auto',
              }}
            >
              Browse Parish Audio VST3 plugins for modern audio production.
            </Typography>

            <Paper
              sx={{
                mt: 1,
                mb: 2,
                backgroundColor: style.color1,
                borderRadius: "8px",
                p: 2,
                boxShadow: 10,
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                sx={{ width: "100%" }}
              >
                <TextField
                  fullWidth
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search plugins..."
                  variant="outlined"
                  size="small"
                  sx={{
                    flex: 1.6,
                    "& .MuiOutlinedInput-root": {
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.03)",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: style.color2,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: style.color3,
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: style.color3,
                      opacity: 1,
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ color: style.color4 }} />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControl
                  size="small"
                  sx={{
                    minWidth: { xs: "100%", md: 210 },
                    "& .MuiOutlinedInput-root": {
                      color: style.color4,
                      backgroundColor: "rgba(255,255,255,0.03)",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      borderColor: style.color2,
                    },
                    "&:hover .MuiOutlinedInput-notchedOutline": {
                      borderColor: style.color3,
                    },
                    "& .MuiSvgIcon-root": {
                      color: style.color4,
                    },
                  }}
                >
                  <InputLabel sx={{ color: style.color4 }}>Sort</InputLabel>
                  <Select
                    value={sortBy}
                    label="Sort"
                    onChange={(e) => setSortBy(e.target.value)}
                  >
                    <MenuItem value="featured">Featured</MenuItem>
                    <MenuItem value="rating">Highest Rated</MenuItem>
                    <MenuItem value="name-asc">Name: A to Z</MenuItem>
                    <MenuItem value="name-desc">Name: Z to A</MenuItem>
                    <MenuItem value="price-low">Price: Low to High</MenuItem>
                    <MenuItem value="price-high">Price: High to Low</MenuItem>
                  </Select>
                </FormControl>
              </Stack>
            </Paper>

            <Typography
              variant="body2"
              sx={{
                color: style.color3,
                mb: 2,
                ml: 0.5,
              }}
            >
              {loading
                ? "Loading plugins..."
                : `${filteredProducts.length} plugin${filteredProducts.length === 1 ? "" : "s"} found`}
            </Typography>

            <Box
              sx={{
                mt: 1,
                backgroundColor: style.color1,
                borderRadius: "8px",
                p: { xs: 1, md: 1.5 },
                mb: 3,
                boxShadow: 10,
              }}
            >
              {loading ? (
                <Box sx={{ py: 6 }}>
                  <LoadingSpinner />
                </Box>
              ) : filteredProducts.length === 0 ? (
                <Box
                  sx={{
                    py: 6,
                    textAlign: "center",
                    color: style.color4,
                  }}
                >
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    No plugins found
                  </Typography>
                  <Typography variant="body2" sx={{ color: style.color3 }}>
                    Try a different search term or filter.
                  </Typography>
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "repeat(2, minmax(0, 1fr))",
                      md: "repeat(3, minmax(0, 1fr))",
                      lg: "repeat(4, minmax(0, 1fr))",
                    },
                    gap: 2,
                    alignItems: "stretch",
                  }}
                >
                  {filteredProducts.map((product, index) => (
                    <Box
                      key={product.SKU || product.productURI || index}
                      sx={{
                        backgroundColor: style.color1,
                        filter: "brightness(1.05)",
                        boxShadow: 10,
                        p: 1,
                        borderRadius: "8px",
                        display: "flex",
                      }}
                    >
                      <Box sx={{ width: "100%", display: "flex" }}>
                        <HomePageProductCard productData={product} />
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
}